import LogoImage from '../ui/LogoImage';
import { useState } from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  HomeIcon, CalendarIcon, BookOpenIcon, ChatBubbleLeftIcon,
  PhotoIcon, MapPinIcon, RadioIcon, MegaphoneIcon,
  UsersIcon, Cog6ToothIcon, ArrowLeftStartOnRectangleIcon,
  Bars3Icon, XMarkIcon, StarIcon, AcademicCapIcon,
} from '@heroicons/react/24/outline';

const navItems = [
  { to: '/admin',              label: 'Dashboard',       icon: HomeIcon,          module: null },
  { to: '/admin/events',       label: 'Events',           icon: CalendarIcon,      module: 'events' },
  { to: '/admin/quotes',       label: 'Murshid Quotes',   icon: StarIcon,          module: 'quotes' },
  { to: '/admin/islamiyah',    label: 'Islamiyah',        icon: AcademicCapIcon,   module: 'islamiyah' },
  { to: '/admin/library',      label: 'Library',          icon: BookOpenIcon,      module: 'library' },
  { to: '/admin/gallery',      label: 'Gallery',          icon: PhotoIcon,         module: 'gallery' },
  { to: '/admin/sawia',        label: 'Sawia Locations',  icon: MapPinIcon,        module: 'sawia' },
  { to: '/admin/radio',        label: 'Radio (Online)',   icon: RadioIcon,         module: 'radio' },
  { to: '/admin/terrestrial',  label: 'Radio Stations',   icon: RadioIcon,         module: 'radio' },
  { to: '/admin/announcements',label: 'Announcements',    icon: MegaphoneIcon,     module: 'announcements' },
  { to: '/admin/users',        label: 'Users',            icon: UsersIcon,         module: 'users', superOnly: true },
  { to: '/admin/settings',     label: 'Settings',         icon: Cog6ToothIcon,     module: 'settings' },
];

export default function AdminLayout({ children }) {
  const { user, logout, can, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/admin/login'); };

  const visibleItems = navItems.filter(item => {
    if (item.superOnly) return isSuperAdmin;
    if (!item.module) return true;
    return can(item.module);
  });

  const Sidebar = () => (
    <div className="flex flex-col h-full bg-navy-900 text-white">
      {/* Logo */}
      <div className="flex items-center gap-3 px-5 py-5 border-b border-white/10">
        <LogoImage size={36} className="rounded-full border border-cyan-400" />
        <div>
          <p className="font-bold text-sm">JNF Admin</p>
          <p className="text-cyan-400 text-xs capitalize">{user?.role?.replace('_', ' ')}</p>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {visibleItems.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to} to={to} end={to === '/admin'}
            onClick={() => setSidebarOpen(false)}
            className={({ isActive }) =>
              `admin-sidebar-link ${isActive
                ? 'bg-cyan-400 text-navy-900 font-semibold'
                : 'text-white/70 hover:text-white hover:bg-white/10'}`
            }
          >
            <Icon className="h-5 w-5 flex-shrink-0" />
            {label}
          </NavLink>
        ))}
      </nav>

      {/* User + Logout */}
      <div className="px-3 py-4 border-t border-white/10 space-y-2">
        <div className="px-4 py-2">
          <p className="text-sm font-medium truncate">{user?.full_name}</p>
          <p className="text-xs text-white/50 truncate">{user?.email}</p>
        </div>
        <Link to="/" className="admin-sidebar-link text-white/60 hover:text-white hover:bg-white/10 text-xs">
          ← View Public Site
        </Link>
        <button onClick={handleLogout} className="admin-sidebar-link text-red-400 hover:text-red-300 hover:bg-red-500/10 w-full text-left">
          <ArrowLeftStartOnRectangleIcon className="h-5 w-5" />
          Logout
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Desktop sidebar */}
      <div className="hidden lg:flex lg:flex-shrink-0 lg:w-60">
        <div className="flex flex-col w-full"><Sidebar /></div>
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-64 z-50"><Sidebar /></div>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top bar (mobile) */}
        <div className="lg:hidden flex items-center gap-3 px-4 py-3 bg-navy-900 border-b border-white/10">
          <button onClick={() => setSidebarOpen(true)} className="text-white p-1">
            <Bars3Icon className="h-6 w-6" />
          </button>
          <p className="text-white font-semibold text-sm">JNF Admin</p>
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
